import React from "react";
import { Box, Typography } from "@mui/material";
import { FN } from "./facilityNetworkTheme";
import type { OverviewTrendYear } from "./facilityNetworkOverviewAggregateUtils";
import { formatOverviewCurrency } from "./facilityNetworkOverviewAggregateUtils";

type Props = {
  years: OverviewTrendYear[];
  loading?: boolean;
};

/** Simple CSS bar chart for aggregated historic penalties (last ≤6 years). */
export default function FacilityNetworkPenaltyTrendChart({ years, loading }: Props) {
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 255,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
        }}
      >
        <Typography sx={{ fontSize: 13, color: FN.muted }}>Loading trend…</Typography>
      </Box>
    );
  }

  if (!years.length) {
    return (
      <Box
        sx={{
          minHeight: 255,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
        }}
      >
        <Typography sx={{ fontSize: 13, color: FN.muted, textAlign: "center" }}>
          No historic penalty data for linked facilities yet
        </Typography>
      </Box>
    );
  }

  const max = Math.max(...years.map((year) => year.amount), 1);

  return (
    <Box
      sx={{
        minHeight: 255,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-around",
        gap: 1,
        px: 2,
        pb: 2,
        pt: 3,
      }}
    >
      {years.map((year) => {
        const heightPct = Math.max(4, Math.round((year.amount / max) * 100));
        return (
          <Box
            key={year.year}
            sx={{
              flex: 1,
              maxWidth: 56,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.75,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                color: FN.figmaAccent,
                lineHeight: 1.2,
                textAlign: "center",
              }}
            >
              {formatOverviewCurrency(year.amount)}
            </Typography>
            <Box
              sx={{
                width: "100%",
                height: 160,
                display: "flex",
                alignItems: "flex-end",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: `${heightPct}%`,
                  borderRadius: "6px 6px 2px 2px",
                  background: "linear-gradient(180deg, #58c2d8 0%, #0a829d 100%)",
                  minHeight: 8,
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: FN.figmaNeutral,
                lineHeight: 1,
              }}
            >
              {year.year}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
